// Block J (UI-22, Projects): the presentational pieces of Projects, on the v2 kit and modelled on
// the Team board (Team/TaskViews.jsx). ProjectTracker.js owns the data, the filters and every
// request; these draw what they are handed and call back.
//
// Every move on the board has a tap route: "Move to…" on each card opens the kit's MoveToSheet,
// which calls the same move code as a drag (addendum 7).
import React from 'react';
import { Avatar, AvatarStack, Button, Card, EmptyState, ProgressBar, Select, StatusPill, StatTile, AddTaskButton } from '../../ui';
import { formatTaskDay, isTaskOverdue as isPastDue } from '../../utils/taskTime';
import { personTint } from '../Team/teamPeople';
import { priorityKey } from '../Team/TaskViews';

// Block 216: the project-task status vocabulary, matched to what the data actually holds.
export const TASK_STATUSES = ['todo', 'in_progress', 'done'];
export const STATUS_DONE = 'done';
export const STATUS_LABEL = { todo: 'To do', in_progress: 'In progress', done: 'Done' };
export const STATUS_TONE = { todo: 'neutral', in_progress: 'pending', done: 'success' };
export const STATUS_TINT = { todo: 'pastel-yellow', in_progress: 'pastel-blue', done: 'pastel-mint' };
const STAGE_TINTS = ['pastel-yellow', 'pastel-blue', 'pastel-lilac', 'pastel-peach', 'pastel-mint', 'pastel-pink'];
export const PROJECT_STATUS_LABEL = { active: 'Active', 'on hold': 'On hold', completed: 'Completed' };
const PROJECT_STATUS_TONE = { active: 'success', 'on hold': 'pending', completed: 'neutral' };

// One definition of "still outstanding" and "overdue" (Block I: through the app's one overdue rule).
export const isTaskDone = (t) => t?.status === STATUS_DONE;
export const isTaskOutstanding = (t) => !isTaskDone(t);
export const isTaskOverdue = (t, tz) => !!t?.dueDate && !isTaskDone(t) && isPastDue({ deadline: t.dueDate }, tz);
export const isProjectOverdue = (p, tz) => !!p?.deadline && p.status === 'active' && isPastDue({ deadline: p.deadline }, tz);
export const isInternal = (p) => (p?.projectType || p?.project_type) === 'internal';
export const minutesOf = (t) => (t?.timeTracked || []).reduce((s, e) => s + (Number(e?.minutes) || 0), 0);
export const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

export function formatMinutes(minutes) {
  if (!minutes) return '0m';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? `${h}h${m > 0 ? ` ${m}m` : ''}` : `${m}m`;
}

// "9 Oct", or '' (a date-only due date, through the shared task formatter).
export const dayText = (d) => (d ? formatTaskDay(d) : '');

// A project's colour is the owner's own choice (stored data), shown as a dot.
export function ProjectDot({ color }) {
  return <span className="pj-dot" style={color ? { '--pj-dot': color } : undefined} aria-hidden="true" />;
}

export function ProjectStatusPill({ status }) {
  return <StatusPill tone={PROJECT_STATUS_TONE[status] || 'neutral'}>{PROJECT_STATUS_LABEL[status] || status}</StatusPill>;
}

export function People({ ids = [], rosterMap, size = 24 }) {
  if (!ids.length) return null;
  const people = ids.map((id) => ({ name: rosterMap.get(id)?.name || 'Former member', color: personTint(id) }));
  return (
    <span className="pj-who" title={people.map((p) => p.name).join(', ')}>
      <AvatarStack size={size} max={3} people={people} />
    </span>
  );
}

// Whole days from today to a date-only deadline (negative once it has passed).
function daysUntil(deadline) {
  const due = new Date(`${deadline}T00:00`);
  if (Number.isNaN(due.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((due - today) / 86400000);
}

function ProjectDue({ project, tz }) {
  if (!project.deadline) return null;
  const days = daysUntil(project.deadline);
  if (isProjectOverdue(project, tz)) {
    return <StatusPill tone="warning">{days !== null && days < 0 ? `${plural(-days, 'day')} overdue` : 'Overdue'}</StatusPill>;
  }
  if (days === 0) return <StatusPill tone="pending">Due today</StatusPill>;
  if (days !== null && days > 0) return <StatusPill tone={days <= 7 ? 'pending' : 'neutral'}>{`${plural(days, 'day')} left`}</StatusPill>;
  return <StatusPill tone="neutral">{`Due ${dayText(project.deadline)}`}</StatusPill>;
}

// ── Landing ──────────────────────────────────────────────────────────────────

// tiles: [{ label, value, hint?, color, onClick?, pressed? }]
export function KpiTiles({ tiles }) {
  return (
    <div className="pj-kpis">
      {tiles.map((t) => (t.onClick ? (
        <button key={t.label} type="button" className="pj-kpi" onClick={t.onClick} aria-pressed={t.pressed === undefined ? undefined : !!t.pressed}>
          <StatTile card color={t.color} label={t.label} value={t.value} hint={t.hint} />
        </button>
      ) : (
        <div key={t.label} className="pj-kpi">
          <StatTile card color={t.color} label={t.label} value={t.value} hint={t.hint} />
        </div>
      )))}
    </div>
  );
}

export function ProjectCard({ project, stats, tz, rosterMap, onOpen }) {
  const pct = stats.totalTasks > 0 ? Math.round((stats.completedTasks / stats.totalTasks) * 100) : 0;
  const internal = isInternal(project);
  const subtitle = internal ? (project.category || 'Internal') : project.client;
  return (
    <Card className="pj-card" color={internal ? 'pastel-lilac' : 'pastel-blue'} label={internal ? 'Internal project' : 'Client project'}
      meta={<ProjectStatusPill status={project.status} />}>
      <div className="pj-card__head">
        <ProjectDot color={project.color} />
        <div className="pj-card__text">
          <button type="button" className="pj-card__open" onClick={() => onOpen(project)}>{project.name}</button>
          {subtitle && <span className="pj-card__sub">{subtitle}</span>}
        </div>
      </div>
      <ProgressBar label="Progress" value={pct} valueText={`${stats.completedTasks} of ${plural(stats.totalTasks, 'task')} done`} />
      <div className="pj-card__foot">
        <People ids={project.team || []} rosterMap={rosterMap} />
        <ProjectDue project={project} tz={tz} />
      </div>
    </Card>
  );
}

// ── Tasks: board, list and by priority ──────────────────────────────────────

function TaskCard({ task, tz, rosterMap, dragging, onDragStart, onDragEnd, onOpen, onMove }) {
  const pk = priorityKey(task.priority);
  const overdue = isTaskOverdue(task, tz);
  const mins = minutesOf(task);
  return (
    <article className={`pj-task${isTaskDone(task) ? ' pj-task--done' : ''}`} draggable="true" data-dragging={dragging ? 'true' : undefined}
      onDragStart={(e) => onDragStart(e, task.id)} onDragEnd={onDragEnd}>
      <button type="button" className="pj-task__open" onClick={() => onOpen(task.id)}>
        <span className="pj-task__title">{task.title}</span>
      </button>
      {(task.dueDate || pk || (task.tags || []).length > 0 || mins > 0) && (
        <div className="pj-task__meta">
          {task.dueDate && <StatusPill tone={overdue ? 'warning' : 'neutral'}>{`${overdue ? 'Overdue, ' : 'Due '}${dayText(task.dueDate)}`}</StatusPill>}
          {pk && <StatusPill priority={pk} />}
          {(task.tags || []).slice(0, 2).map((tag) => <StatusPill key={tag} tone="neutral">{tag}</StatusPill>)}
          {mins > 0 && <StatusPill tone="neutral">{`${formatMinutes(mins)} logged`}</StatusPill>}
        </div>
      )}
      <div className="pj-task__foot">
        <People ids={task.assignedTo || []} rosterMap={rosterMap} />
        <Button size="sm" variant="tertiary" aria-label={`Move "${task.title}" to…`} onClick={() => onMove(task)}>Move to…</Button>
      </div>
    </article>
  );
}

// The board's columns: the project's workflow stages when it has them, else the three statuses.
export function boardColumns(stages = []) {
  if (stages.length > 0) {
    return stages.map((s, i) => ({ id: s.id, key: s.id, label: s.name, tint: STAGE_TINTS[i % STAGE_TINTS.length], stage: s }));
  }
  return TASK_STATUSES.map((key) => ({ id: null, key, label: STATUS_LABEL[key], tint: STATUS_TINT[key] }));
}

// The column a task is in. A task with no stage sits in the start stage (or the first).
export function columnOf(task, columns, stages = []) {
  if (stages.length > 0) {
    const startId = stages.find((s) => s.isStart)?.id ?? stages[0]?.id ?? null;
    return columns.find((c) => c.id === (task.stageId || startId)) || null;
  }
  return columns.find((c) => c.key === task.status) || null;
}

export function KanbanView({ tasks, columns, stages, tz, rosterMap, draggingId, onDragStart, onDragEnd, onDrop, onOpen, onMove }) {
  const [over, setOver] = React.useState(null);
  return (
    <div className="pj-board">
      {columns.map((col) => {
        const colTasks = tasks.filter((t) => columnOf(t, columns, stages)?.key === col.key);
        return (
          <section key={col.key} className="pj-col" aria-label={col.label} data-stage={col.id || col.key}
            data-drop={over === col.key ? 'true' : undefined} style={{ '--pj-col': `var(--${col.tint})` }}
            onDragOver={(e) => { e.preventDefault(); if (over !== col.key) setOver(col.key); }}
            onDragLeave={() => setOver((o) => (o === col.key ? null : o))}
            onDrop={(e) => { setOver(null); onDrop(e, col); }}>
            <div className="pj-col__head">
              <h3 className="pj-col__title">{col.label}</h3>
              <StatusPill tone="neutral">{colTasks.length}</StatusPill>
            </div>
            <div className="pj-col__body">
              {colTasks.length === 0
                ? <p className="pj-col__empty">Nothing here yet.</p>
                : colTasks.map((t) => (
                  <TaskCard key={t.id} task={t} tz={tz} rosterMap={rosterMap} dragging={draggingId === t.id}
                    onDragStart={onDragStart} onDragEnd={onDragEnd} onOpen={onOpen} onMove={onMove} />
                ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}

function StatusSelect({ task, onChange }) {
  return (
    <Select className="pj-line__status" aria-label={`Status of "${task.title}"`} value={task.status}
      onChange={(e) => onChange(task.id, e.target.value)}
      options={[
        ...TASK_STATUSES.map((s) => ({ value: s, label: STATUS_LABEL[s] })),
        // Block 216: an unknown stored value still shows, so it is visible rather than hidden.
        ...(TASK_STATUSES.includes(task.status) ? [] : [{ value: task.status, label: String(task.status) }]),
      ]} />
  );
}

function TaskLine({ task, tz, rosterMap, onOpen, onStatusChange, showPriority = true }) {
  const pk = priorityKey(task.priority);
  const overdue = isTaskOverdue(task, tz);
  const mins = minutesOf(task);
  const sub = [
    task.dueDate ? `${overdue ? 'Overdue, was due' : 'Due'} ${dayText(task.dueDate)}` : '',
    mins > 0 ? `${formatMinutes(mins)} logged` : '',
  ].filter(Boolean).join(' · ');
  return (
    <li className="pj-line">
      <button type="button" className="pj-line__main" onClick={() => onOpen(task.id)}>
        <span className="pj-task__title">{task.title}</span>
        {sub && <span className="pj-line__sub">{sub}</span>}
      </button>
      <People ids={task.assignedTo || []} rosterMap={rosterMap} />
      <span className="pj-line__pills">
        {overdue && <StatusPill tone="warning">Overdue</StatusPill>}
        {showPriority && pk && <StatusPill priority={pk} />}
      </span>
      <StatusSelect task={task} onChange={onStatusChange} />
    </li>
  );
}

export function TaskListView({ groups, filtered, tz, rosterMap, onOpen, onStatusChange, onNewTask, children }) {
  const total = groups.reduce((n, g) => n + g.tasks.length, 0);
  return (
    <div className="pj-stack pj-stack--tight">
      {total === 0 && (
        filtered
          ? <EmptyState title="No tasks match">Try another priority or member, or clear the search.</EmptyState>
          : <EmptyState title="No tasks yet" action={onNewTask && <AddTaskButton onClick={onNewTask} />}>Add the first task for this project.</EmptyState>
      )}
      {total > 0 && groups.map((g) => (
        <section key={g.id ?? 'none'} className="pj-group" aria-label={g.name || 'Tasks'}>
          {g.name && <h3 className="pj-group__head">{g.name} <StatusPill tone="neutral">{g.tasks.length}</StatusPill></h3>}
          {g.tasks.length === 0
            ? <p className="pj-muted">No tasks in this section.</p>
            : (
              <ul className="pj-lines">
                {g.tasks.map((t) => <TaskLine key={t.id} task={t} tz={tz} rosterMap={rosterMap} onOpen={onOpen} onStatusChange={onStatusChange} />)}
              </ul>
            )}
        </section>
      ))}
      {children}
    </div>
  );
}

const PRIORITY_TINT = { High: 'priority-high', Medium: 'priority-medium', Low: 'priority-low' };

export function PriorityView({ tasks, filtered, tz, rosterMap, onOpen, onStatusChange }) {
  const groups = ['High', 'Medium', 'Low']
    .map((p) => ({ priority: p, tasks: tasks.filter((t) => priorityKey(t.priority) === p.toLowerCase()) }))
    .filter((g) => g.tasks.length > 0);
  if (tasks.length === 0) {
    return filtered
      ? <EmptyState title="No tasks match">Try another priority or member, or clear the search.</EmptyState>
      : <EmptyState title="No tasks yet">Add the first task for this project.</EmptyState>;
  }
  return (
    <div className="pj-stack pj-stack--tight">
      {groups.map((g) => (
        <Card key={g.priority} lift={false} color={PRIORITY_TINT[g.priority]} label={`${g.priority} priority`} meta={plural(g.tasks.length, 'task')}>
          <ul className="pj-lines">
            {g.tasks.map((t) => <TaskLine key={t.id} task={t} tz={tz} rosterMap={rosterMap} onOpen={onOpen} onStatusChange={onStatusChange} showPriority={false} />)}
          </ul>
        </Card>
      ))}
    </div>
  );
}

// ── Overview tab ────────────────────────────────────────────────────────────

export function OverviewTab({ project, stats, hoursLogged, members, onEdit }) {
  const pct = stats.totalTasks > 0 ? Math.round((stats.completedTasks / stats.totalTasks) * 100) : 0;
  const internal = isInternal(project);
  const facts = [
    ['Status', <ProjectStatusPill status={project.status} />],
    ['Type', internal ? 'Internal' : 'Client'],
    internal ? ['Category', project.category || 'None'] : ['Client', project.client || 'None'],
    ['Start date', project.startDate ? dayText(project.startDate) : 'Not set'],
    ['Due date', project.deadline ? dayText(project.deadline) : 'Not set'],
    ...(!internal ? [['Budget', project.budget ? `£${Number(project.budget).toLocaleString('en-GB')}` : 'Not set']] : []),
  ];
  const team = (project.team || []).map((id) => members.find((u) => u.id === id)).filter(Boolean);
  return (
    <div className="pj-cards">
      <Card lift={false} color="pastel-blue" label="Project details" footer={<Button variant="secondary" onClick={onEdit}>Edit project</Button>}>
        <dl className="pj-facts">
          {facts.map(([label, value]) => (
            <div key={label}><dt>{label}</dt><dd>{value}</dd></div>
          ))}
        </dl>
        {project.description && (
          <>
            <span className="pj-drawer__label">Description</span>
            <p className="pj-desc">{project.description}</p>
          </>
        )}
      </Card>
      <div className="pj-stack pj-stack--tight">
        <Card lift={false} color="pastel-mint" label="Progress">
          <ProgressBar label="Task completion" value={pct} />
          <div className="pj-tiles">
            <StatTile label="Total tasks" value={stats.totalTasks} />
            <StatTile label="Completed" value={stats.completedTasks} />
            <StatTile label="In progress" value={stats.inProgressTasks} />
            <StatTile label="Hours logged" value={`${hoursLogged}h`} />
          </div>
        </Card>
        {team.length > 0 && (
          <Card lift={false} color="pastel-lilac" label="Team" meta={plural(team.length, 'person')}>
            <ul className="pj-people">
              {team.map((u) => (
                <li key={u.id}>
                  <Avatar name={u.name} size={36} color={personTint(u.id)} />
                  <span>
                    <span className="pj-people__name">{u.name}</span>
                    {u.jobTitle && <span className="pj-people__sub"> · {u.jobTitle}</span>}
                  </span>
                </li>
              ))}
            </ul>
          </Card>
        )}
      </div>
    </div>
  );
}
